import { useCallback, useEffect, useState } from "react";
import { Link2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { PrescripteurSelect, usePrescripteurs } from "@/components/PrescripteurSelect";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";

function Groupe({ g, idx, comptes, onDone }) {
  const [compte, setCompte] = useState(null);
  const [sel, setSel] = useState(() => new Set(g.demandes.map((d) => d.id)));
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const relier = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/demandes/prescripteurs/relier", { prescripteur: g.prescripteur, prescripteur_id: compte.id, demande_ids: [...sel] });
      toast.success(`${data.relies} dossier(s) relié(s) au compte de ${data.prescripteur}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-4 space-y-3" data-testid={`orphelin-group-${idx}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div><span className="font-semibold text-slate-900">{g.prescripteur}</span>
          <span className="ml-2 text-xs text-slate-500">{g.count} dossier(s){g.courriels.length ? ` · ${g.courriels.join(", ")}` : ""}</span></div>
      </div>
      <div className="flex flex-wrap gap-2">
        {g.demandes.map((d) => (
          <label key={d.id} className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2 py-1 text-xs cursor-pointer hover:bg-slate-50">
            <Checkbox checked={sel.has(d.id)} onCheckedChange={() => toggle(d.id)} data-testid={`orphelin-check-${d.numero}`} /> {d.numero}
          </label>
        ))}
      </div>
      <div className="grid md:grid-cols-[1fr_auto] gap-2 items-start">
        <PrescripteurSelect list={comptes} value={compte?.id} onChange={setCompte} testId={`orphelin-select-${idx}`} />
        <Button disabled={!compte || !sel.size || busy} onClick={relier} style={{ background: "#0284C7" }} data-testid={`orphelin-link-${idx}`}>
          <Link2 className="h-4 w-4 mr-1" /> Relier {sel.size} dossier(s)
        </Button>
      </div>
    </Card>
  );
}

export default function RelierPrescripteurs() {
  const [groups, setGroups] = useState(null);
  const comptes = usePrescripteurs();
  const load = useCallback(() => { api.get("/demandes/prescripteurs/orphelins").then((r) => setGroups(r.data)).catch(() => setGroups([])); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-5" data-testid="relier-prescripteurs-page">
      <PageHeader title="Relier les anciens dossiers" subtitle="Dossiers sans compte relié, regroupés par nom de médecin prescripteur. Vérifiez chaque dossier avant de relier : deux médecins peuvent porter le même nom.">
        <Link to="/demandes" className="text-sm text-sky-700 flex items-center gap-1" data-testid="relier-back-link"><ArrowLeft className="h-4 w-4" /> Demandes</Link>
      </PageHeader>
      {groups === null ? <div className="text-slate-400 text-sm">Chargement…</div>
        : groups.length === 0 ? <Card className="p-6 text-sm text-slate-500" data-testid="relier-empty">Tous les dossiers sont reliés à un compte.</Card>
        : groups.map((g, i) => <Groupe key={`${g.prescripteur}:${g.demandes.map((d) => d.id).join(",")}`} g={g} idx={i} comptes={comptes} onDone={load} />)}
    </div>
  );
}
